import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import Lightbox from '../components/Lightbox';
import { imageUrl } from '../lib/images';

export default function NewsDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    async function fetchArticle() {
      setLoading(true);
      try {
        const res = await fetch('/_api/public/content/nyheder');
        if (res.ok) {
          const data = await res.json();
          const items = Array.isArray(data) ? data : data?.items || [];
          const found = items.find(
            (item) =>
              item.slug === slug ||
              item.slug?._ === slug ||
              item.id === slug ||
              (typeof item.slug === 'object' && Object.values(item.slug).includes(slug))
          );
          setArticle(found || null);
        }
      } catch (err) {
        console.warn('Error fetching article:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchArticle();
  }, [slug]);

  if (loading) {
    return (
      <div className="py-24 text-center text-slate-500 text-sm">
        <i className="fa-solid fa-spinner fa-spin text-2xl text-sky-600 mb-3 block" aria-hidden="true" />
        Henter nyhed...
      </div>
    );
  }

  if (!article) {
    return (
      <div className="py-20 max-w-lg mx-auto px-4 text-center">
        <h2 className="text-2xl font-bold text-[#0f2b48] mb-3">Nyheden blev ikke fundet</h2>
        <p className="text-slate-600 text-sm mb-6">
          Den ønskede nyhed findes ikke, eller er blevet afpubliceret.
        </p>
        <Link
          to="/nyheder"
          className="inline-flex items-center px-5 py-2.5 rounded-lg bg-[#0f2b48] text-white text-sm font-semibold hover:bg-sky-900 transition-colors"
        >
          <i className="fa-solid fa-arrow-left mr-2" aria-hidden="true" />
          <span>Tilbage til alle nyheder</span>
        </Link>
      </div>
    );
  }

  const title = article.titel || 'Nyhed';
  
  // Robust fallback for SEO Title if empty
  const seoTitle = article.seo_title?.trim()
    ? article.seo_title.trim()
    : `${title} | Fastlane ApS`;

  // Robust fallback for Meta Description if empty:
  // 1. article.meta_description
  // 2. article.intro
  // 3. Cleaned excerpt from article.broedtekst
  // 4. Default high-relevance fallback
  const cleanBodyText = article.broedtekst
    ? article.broedtekst
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/\s+/g, ' ')
        .trim()
    : '';

  const fallbackDesc = article.intro?.trim() || cleanBodyText || 'Læs seneste nyheder og opdateringer fra Fastlane ApS inden for shipping, fragt og spedition.';
  const metaDesc = (article.meta_description?.trim() || fallbackDesc).slice(0, 160).trim();
  const dateStr = article.dato
    ? new Date(article.dato).toLocaleDateString('da-DK', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    : '';
  const mainImage = article.hovedbillede?.[0] || article.hovedbillede;
  const extraImages = article.ekstra_billeder || [];

  return (
    <>
      <SEO
        title={seoTitle}
        description={metaDesc}
        canonicalUrl={`https://fastlane.dk/nyheder/${slug}`}
        ogImage={mainImage}
      />

      <PageHeader
        title={title}
        subtitle={dateStr ? `Udgivet den ${dateStr}` : ''}
        breadcrumbs={[
          { label: 'Nyheder', path: '/nyheder' },
          { label: title }
        ]}
      />

      <article className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back button */}
          <Link
            to="/nyheder"
            className="inline-flex items-center text-sm font-semibold text-sky-700 hover:text-sky-900 transition-colors mb-8 group"
          >
            <i className="fa-solid fa-arrow-left text-xs mr-2 group-hover:-translate-x-1 transition-transform" aria-hidden="true" />
            <span>Tilbage til nyhedsoversigten</span>
          </Link>

          {/* Main Hero Image */}
          {mainImage && (
            <div className="w-fit max-w-full rounded-2xl overflow-hidden shadow-md mb-8 bg-slate-100 border border-slate-200/80">
              <img
                src={imageUrl(mainImage, 'width=1200,quality=85,format=auto')}
                alt={title}
                className="max-h-[350px] w-auto max-w-full object-cover block"
              />
            </div>
          )}

          {/* Intro highlight */}
          {article.intro && (
            <p className="text-lg sm:text-xl font-medium text-slate-800 leading-relaxed mb-6 border-l-4 border-sky-600 pl-4 py-1 bg-sky-50/40 rounded-r-lg">
              {article.intro}
            </p>
          )}

          {/* RichText Body */}
          {article.broedtekst && (
            <div
              className="prose prose-slate max-w-none text-slate-700 leading-relaxed text-base space-y-4"
              dangerouslySetInnerHTML={{ __html: article.broedtekst }}
            />
          )}

          {/* Extra Images Gallery */}
          {extraImages.length > 0 && (
            <div className="mt-12 pt-8 border-t border-slate-200">
              <h3 className="text-xl font-bold text-[#0f2b48] mb-4">Flere billeder</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {extraImages.map((img, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setLightboxIndex(idx)}
                    className="aspect-4/3 rounded-xl overflow-hidden bg-slate-100 border border-slate-200 shadow-2xs hover:shadow-md transition-all group focus:outline-hidden cursor-pointer"
                  >
                    <img
                      src={imageUrl(img, 'width=600,quality=80,format=auto')}
                      alt={`${title} foto ${idx + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Contact / CTA box */}
          <div className="mt-12 p-6 sm:p-8 bg-slate-50 rounded-2xl border border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h4 className="text-lg font-bold text-[#0f2b48]">Har du spørgsmål til artiklen eller vores ydelser?</h4>
              <p className="text-xs sm:text-sm text-slate-600 mt-1">
                Kontakt Fastlane ApS på Storstrømsvej i Esbjerg for mere information.
              </p>
            </div>
            <Link
              to="/kontakt"
              className="inline-flex items-center px-6 py-3 rounded-lg bg-[#b25d19] hover:bg-[#974d12] text-white text-sm font-semibold transition-colors shrink-0"
            >
              <span>Kontakt os</span>
              <i className="fa-solid fa-arrow-right ml-2 text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </article>

      {/* Lightbox for extra images */}
      {lightboxIndex !== null && (
        <Lightbox
          images={extraImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={(newIdx) => setLightboxIndex(newIdx)}
        />
      )}
    </>
  );
}
